/**
 * ProductLinker
 *
 * Lets merchants link Shopify products to a template using Shopify's native
 * resource picker (App Bridge 4). When an order containing a linked product
 * is placed, SignShield auto-triggers a signing request.
 */
import { useCallback, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  InlineStack,
  Text,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import type { TemplateProductLink } from '../types';

interface Props {
  templateId: string;
  initialLinks: TemplateProductLink[];
  onSaved: (links: TemplateProductLink[]) => void;
}

/** Extract the numeric Shopify product ID from a GID like "gid://shopify/Product/123". */
function gidToNumericId(gid: string): string {
  return gid.split('/').pop() ?? gid;
}

export default function ProductLinker({ templateId, initialLinks, onSaved }: Props) {
  const { request } = useApi();

  const [linked, setLinked] = useState<TemplateProductLink[]>(initialLinks);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPicker = useCallback(async () => {
    setPicking(true);
    try {
      // Pre-select already-linked products so the merchant sees what's chosen.
      const selectionIds = linked.map((l) => ({
        id: `gid://shopify/Product/${l.shopifyProductId}`,
      }));

      const selected = await shopify.resourcePicker({
        type: 'product',
        multiple: true,
        selectionIds,
      });

      // undefined means the merchant closed the picker without confirming.
      if (selected === undefined) return;

      setLinked(
        selected.map((p) => ({
          id: `_${gidToNumericId(p.id)}`,
          templateId,
          shopifyProductId: gidToNumericId(p.id),
          shopifyProductTitle: p.title,
        })),
      );
    } catch (e: any) {
      setError(e.message ?? 'Failed to open product picker');
    } finally {
      setPicking(false);
    }
  }, [linked, templateId]);

  const removeProduct = useCallback((productId: string) => {
    setLinked((prev) => prev.filter((l) => l.shopifyProductId !== productId));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await request<TemplateProductLink[]>(
        `/templates/${templateId}/products`,
        {
          method: 'PUT',
          body: JSON.stringify({
            products: linked.map((l) => ({
              shopifyProductId: l.shopifyProductId,
              shopifyProductTitle: l.shopifyProductTitle,
            })),
          }),
        },
      );
      setLinked(saved);
      onSaved(saved);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BlockStack gap="400">
      <BlockStack gap="100">
        <Text variant="headingSm" as="h3">Linked products</Text>
        <Text variant="bodySm" tone="subdued" as="p">
          When an order containing one of these products is placed, SignShield
          automatically sends a signing request to the customer.
        </Text>
      </BlockStack>

      {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

      {/* Currently linked products */}
      {linked.length > 0 ? (
        <BlockStack gap="200">
          <Text variant="bodySm" fontWeight="semibold" as="p">
            {linked.length} product{linked.length !== 1 ? 's' : ''} linked
          </Text>
          <div style={{ border: '1px solid var(--p-color-border)', borderRadius: 8, overflow: 'hidden' }}>
            {linked.map((link) => (
              <div
                key={link.shopifyProductId}
                style={{
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid var(--p-color-border)',
                }}
              >
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Linked</Badge>
                  <Text as="span" variant="bodyMd">
                    {link.shopifyProductTitle ?? link.shopifyProductId}
                  </Text>
                </InlineStack>
                <Button
                  size="micro"
                  tone="critical"
                  onClick={() => removeProduct(link.shopifyProductId)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </BlockStack>
      ) : (
        <Text tone="subdued" variant="bodySm" as="p">No products linked yet.</Text>
      )}

      <InlineStack gap="300" align="space-between" blockAlign="center">
        <Button loading={picking} onClick={openPicker}>
          {linked.length > 0 ? 'Edit product selection' : 'Add products'}
        </Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>
          Save product links
        </Button>
      </InlineStack>
    </BlockStack>
  );
}
